import { useState } from "react";
import { useProfitTrends } from "@/context/ProfitTrendsContext";
import { useFunds } from "@/context/FundsContext";
import { LoadingView } from "@/components";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendsChart, PeriodTab, ValueMode, periodConfig } from "./components/TrendsChart";
import { TrendMetricCards } from "./components/TrendMetricCards";

export const TrendsPage = (): React.ReactNode => {
  const { profitTrends, loading, error } = useProfitTrends();
  const { fundsStats } = useFunds();
  const totalDeposits = fundsStats.totalDeposits ?? 0;
  const [period, setPeriod] = useState<PeriodTab>("month");
  const [valueMode, setValueMode] = useState<ValueMode>("$");

  if (loading) return <LoadingView />;

  if (error) {
    return (
      <div className="grid gap-6">
        <h1 className="text-3xl font-bold">Trends</h1>
        <p className="text-red-500">Error: {error}</p>
      </div>
    );
  }

  if (!profitTrends) {
    return (
      <div className="grid gap-6">
        <h1 className="text-3xl font-bold">Trends</h1>
        <p className="text-muted-foreground">No data available</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Trends</h1>
          <p className="text-muted-foreground mt-2">Profit trends and performance over time</p>
        </div>
        <div className="flex gap-2">
          <Tabs value={period} onValueChange={(v) => setPeriod(v as PeriodTab)}>
            <TabsList className="h-8">
              {(Object.keys(periodConfig) as PeriodTab[]).map((key) => (
                <TabsTrigger key={key} value={key} className="px-3 py-1 text-xs">{periodConfig[key].label}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Tabs value={valueMode} onValueChange={(v) => setValueMode(v as ValueMode)}>
            <TabsList className="h-8">
              <TabsTrigger value="$" className="px-3 py-1 text-xs">$</TabsTrigger>
              <TabsTrigger value="%" className="px-3 py-1 text-xs">%</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <TrendMetricCards profitTrends={profitTrends} totalDeposits={totalDeposits} period={period} valueMode={valueMode} />

      <TrendsChart profitTrends={profitTrends} totalDeposits={totalDeposits} period={period} valueMode={valueMode} />
    </div>
  );
};
